/**
 * @file
 * Functions used internally by OkcApi, wrapped for external use by the
 * interface defined in OkcApi/index.js
 */

import getAccessToken from "shared/util/getAccessToken";

import {
	API_PREFIX,
	METHODS,
	DEFAULT_ERROR,
	NO_AJAX_LIB_ERROR,
} from "./constants";

// Fallback for not having jQuery / Zepto in the global space
let axios = null;

if (process.env.PLATFORM === "mobile") {
	axios = require("axios");
}

if (!process.env.PLATFORM) {
	axios = require("axios");
}

// Mobile web / desktop need to be explicit, apps do UA parsing server-side
function getPlatform() {
	if (window.Context && window.Context.get("platform")) {
		return window.Context.get("platform");
	}
	else if (window.App) {
		return null;
	}
	else {
		return "UNKNOWN";
	}
}

// Return if they have jQuery or not
function getJquery() {
	if (window.$ && window.$.ajax) {
		return window.$;
	} else if (window.jQuery && window.jQuery.ajax) {
		return window.jQuery;
	}

	return null;
}

function getDeviceResHeader() {
	const ratio = window.devicePixelRatio || 1;

	// iOS reports css pixels, Android reports screen px
	if (window.App.ios) {
		return [window.screen.width, window.screen.height, ratio].join("x");
	} else {
		return [window.screen.width * ratio, window.screen.height * ratio, ratio].join("x");
	}
}

function buildHeaders(params) {
	const baseHeaders = {};
	const platform = getPlatform();
	const accessToken = getAccessToken();

	if (platform) {
		baseHeaders["x-okcupid-platform"] = platform;
	} else {
		baseHeaders["x-okcupid-app"] = window.App.app_version;
		baseHeaders["x-okcupid-device-id"] = window.App.token;
		baseHeaders["x-okcupid-device-res"] = getDeviceResHeader();
	}

	if (accessToken) {
		baseHeaders.Authorization = `Bearer ${accessToken}`;
	}

	if (params.version) {
		baseHeaders.endpoint_version = params.version;
	}

	return {
		...params.headers,
		...baseHeaders,
	};
}

function jqueryRequest(path, params, resolve, reject) {
	const $ = getJquery();
	const isZepto = typeof window.Zepto !== "undefined";
	let jqPath = path;

	const jqParams = {
		...params,
		type: params.type || METHODS.GET,
		dataType: "json",
		headers: buildHeaders(params),
		success: (res) => {
			if (res && res.error) {
				if (params.error) {
					params.error(res);
				}

				reject(params.error);
			}
			else {
				if (params.success) {
					params.success(res);
				}

				resolve(res);
			}
		},
		error: (res) => {
			const { response } = res;
			let { responseJSON } = res;

			// if we're on Zepto, sometimes it doesn't encode JSON properly
			if (isZepto && !responseJSON && response) {
				try {
					responseJSON = JSON.parse(response);
				} catch (e) {
					// do nothing for now
				}
			}

			let err;

			if (responseJSON) {
				// Don't do anything if the user aborted
				if (res.statusText === "abort") {
					return;
				}

				err = responseJSON;
			} else {
				err = DEFAULT_ERROR;

				if (res.status === 0) {
					err.requestCanceled = true;
				}
			}

			if (params.error) {
				params.error(err, res);
			}

			reject(err, res);
		},
	};

	if (params.data) {
		if (params.type === METHODS.DELETE) {
			// jQuery doesn't auto-convert params for DELETE like it does for GET.
			jqPath = `${jqPath}?${$.param(params.data)}`;
			delete (jqParams.data);
		} else if (params.type === METHODS.POST || params.type === METHODS.PUT) {
			// For POST / PUT, the API speaks in json.
			jqParams.data = JSON.stringify(params.data);
			jqParams.contentType = "application/json; charset=UTF-8";
		}
	}

	$.ajax({
		url: jqPath,
		...jqParams,
	});
}

function axiosRequest(path, params, resolve, reject) {
	const method = params.type || METHODS.GET;
	let axiosData = null;
	let axiosParams = null;

	if (method === METHODS.PUT || method === METHODS.POST) {
		axiosData = params.data;
	}
	else {
		axiosParams = params.data;
	}

	axios({
		method,
		url: path,
		headers: buildHeaders(params),
		data: axiosData,
		params: axiosParams,
	})
		.then((res) => {
		// Well now how did this happen...
			if (!res || !res.data) {
				throw new Error();
			}

			if (res.data.error) {
				if (params.error) {
					params.error(res.data, res);
				}

				reject(res.data, res);
			} else {
				if (params.success) {
					params.success(res.data);
				}

				resolve(res.data);
			}
		})
		.catch((error) => {
			let err;

			if (error.response) {
				if (error.response.data) {
					err = error.response.data;
				} else {
					err = DEFAULT_ERROR;
				}
			} else {
				// this means the call failed
				err = {
					...DEFAULT_ERROR,
					requestCanceled: true,
				};
			}

			if (params.error) {
				params.error(err, error.response);
			}

			reject(err, error.response);
		});
}

export function apiRequest(path, params) {
	const fullPath = `${API_PREFIX}/${path.replace(/^\//, "")}`;

	// Desktop / app have jquery / zepto respectively
	return new Promise((resolve, reject) => {
		if (getJquery()) {
			jqueryRequest(fullPath, params, resolve, reject);
		}

		else if (axios) {
			// But fallback to axios for mobile web, or if apps change
			axiosRequest(fullPath, params, resolve, reject);
		}

		else {
			// Worst case, still fail gracefully
			if (params.error) {
				params.error(NO_AJAX_LIB_ERROR, 0);
			}

			reject(NO_AJAX_LIB_ERROR, 0);
		}
	});
}

export function serializeParameters(p) {
	const pNew = Object.keys(p).reduce((memo, key) => {
		const value = p[key];
		const keyEncoded = encodeURIComponent(key);

		if (Array.isArray(value)) {
			return value.reduce((memoB, val) => (
				[...memoB, `${keyEncoded}=${encodeURIComponent(val)}`]
			), memo);
		}

		return [...memo, `${keyEncoded}=${encodeURIComponent(value)}`];
	}, []).join("&");

	return pNew;
}

export function legacyRequest(path, params) {
	return new Promise((resolve, reject) => {
		const $ = getJquery();

		if ($) {
			// jquery sends null values as empty strings.
			// is bad.
			// so make a new object with only the params we want.
			const data = Object.keys(params.data || {}).reduce((memo, key) => {
				if (params.data[key] === null || params.data[key] === undefined) {
					return memo;
				}

				return {
					...memo,
					[key]: params.data[key],
				};
			}, {});

			$.ajax({
				url: path,
				data,
				type: params.method || params.type || null,
				dataType: "json",
				headers: params.headers,
				traditional: true,
				success: (res) => resolve(res),
				error: (res) => reject(res),
			});
		} else {
			const type = params.type ? params.type.toUpperCase() : "GET";
			const useData = type === METHODS.POST || type === METHODS.PUT;

			axios({
				method: type,
				url: path,
				data: useData ? params.data : null,
				params: useData ? null : params.data,
				headers: params.headers,

				// axios formats array params as key[]=value1&key[]=value2
				// but okws expects key=value1&key=value2
				// so do that transform
				paramsSerializer: serializeParameters,
			})
				.then((res) => resolve(res.data))
				.catch((err) => reject(err));
		}
	});
}

export default {
	apiRequest,
	legacyRequest,
};



// WEBPACK FOOTER //
// ./src/shared/util/OkcApi/internal.js